// holeVisual.ts — turns a SkinDefinition into an animated 3D hole: shader
// abyss, rim, decorations around the rim, ground glow/vortex, ambient
// particles and a movement trail. Used for the player, AI rivals, arena
// opponents and the menu previews.
import * as THREE from "three";
import type { SkinDefinition } from "./skins";
import { Shape, type Effects } from "./particles";

/** Depth of the visible shaft; objects below its floor are hidden by it. */
export const ABYSS_DEPTH = 40;

const ABYSS_VERT = `
varying vec2 vUv;
varying vec3 vLocal;
void main() {
  vUv = uv;
  vLocal = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

// u = around the wall, v = 1 at the rim, 0 at the bottom.
const ABYSS_FRAG = `
uniform float uTime;
uniform vec3 uA;
uniform vec3 uB;
uniform int uKind;
uniform float uDepth;
varying vec2 vUv;
varying vec3 vLocal;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y);
}
float fbm(vec2 p) { float v = 0.0, a = 0.5; for (int i = 0; i < 4; i++) { v += a * noise(p); p *= 2.03; a *= 0.5; } return v; }
void main() {
  // 1 at the rim .. 0 nine units down (everything below is pitch black).
  float depth = clamp(1.0 - (1.0 - vUv.y) * uDepth / 9.0, 0.0, 1.0);
  float u = vUv.x * 6.2831853;
  float shade = pow(depth, 1.6);     // walls darken quickly with depth
  vec3 col = uA;
  if (uKind == 1) {
    // galaxy: swirling nebula + stars
    vec2 q = vec2(vUv.x * 8.0 + uTime * 0.05, depth * 3.0 - uTime * 0.12);
    float n = fbm(q * 1.5 + fbm(q + uTime * 0.03));
    col = mix(uA, uB, smoothstep(0.35, 0.9, n) * 0.9);
    float st = step(0.985, hash(floor(vec2(vUv.x * 160.0, depth * 60.0 + uTime * 2.0))));
    col += st * (0.6 + 0.4 * sin(uTime * 5.0 + vUv.x * 50.0));
    shade = 0.25 + 0.75 * depth;
  } else if (uKind == 2) {
    // lava: glowing cracks flowing downwards
    vec2 q = vec2(vUv.x * 10.0, depth * 4.0 + uTime * 0.35);
    float n = fbm(q);
    float crack = smoothstep(0.08, 0.0, abs(n - 0.5));
    col = mix(uA, uB, crack) + uB * 0.25 * smoothstep(0.6, 1.0, depth);
    shade = 0.35 + 0.65 * depth;
  } else if (uKind == 3) {
    // neon grid scrolling into the depth
    float gx = abs(fract(vUv.x * 24.0) - 0.5);
    float gy = abs(fract(depth * 8.0 + uTime * 0.6) - 0.5);
    float line = smoothstep(0.06, 0.0, min(gx, gy) - 0.0);
    col = uA + uB * line * 1.4;
    shade = 0.2 + 0.8 * depth;
  } else if (uKind == 4) {
    // spiral swirl
    float sp = sin(u * 3.0 + depth * 10.0 - uTime * 2.4);
    col = mix(uA, uB, smoothstep(0.55, 1.0, sp) * 0.75);
  } else if (uKind == 5) {
    // slime: wobbling bubbles
    float n = fbm(vec2(vUv.x * 9.0 + sin(uTime + depth * 4.0) * 0.3, depth * 5.0 - uTime * 0.25));
    col = mix(uA, uB, smoothstep(0.45, 0.75, n) * 0.8);
  } else {
    col = mix(uA, uB, smoothstep(0.55, 1.0, depth) * 0.6);
  }
  // Bottomless: whatever the skin, the shaft turns pitch black ~9 units down.
  float below = (1.0 - depth) * uDepth;
  float abyss = 1.0 - smoothstep(0.5, 9.0, below);
  gl_FragColor = vec4(col * shade * abyss, 1.0);
}`;

const GLOW_VERT = `
varying vec2 vPos;
void main() {
  vPos = position.xy;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

// Ground ring around the rim: soft glow plus rotating suction arms.
const GLOW_FRAG = `
uniform float uTime;
uniform vec3 uColor;
uniform float uStrength;
uniform float uPulse;
varying vec2 vPos;
void main() {
  float r = length(vPos);
  float a = atan(vPos.y, vPos.x);
  float edge = smoothstep(1.55, 1.02, r) * smoothstep(0.98, 1.04, r);
  float arms = 0.5 + 0.5 * sin(a * 6.0 + r * 9.0 - uTime * 3.2);
  float alpha = edge * (0.55 + 0.45 * arms) * uStrength * (1.0 + uPulse);
  gl_FragColor = vec4(uColor * (1.0 + uPulse), alpha);
}`;

const INNER_KIND: Record<SkinDefinition["inner"]["kind"], number> = {
  dark: 0, galaxy: 1, lava: 2, grid: 3, swirl: 4, slime: 5,
};

function decoGeometry(skin: SkinDefinition): THREE.BufferGeometry | undefined {
  switch (skin.deco.kind) {
    case "studs": return new THREE.SphereGeometry(0.055, 10, 8);
    case "spikes": return new THREE.ConeGeometry(0.07, 0.32, 6).translate(0, 0.16, 0);
    case "crystals": return new THREE.OctahedronGeometry(0.11, 0).scale(0.7, 2.4, 0.7).translate(0, 0.2, 0);
    case "petals": return new THREE.SphereGeometry(0.14, 10, 6).scale(1.6, 0.25, 0.8);
    case "pepperoni": return new THREE.CylinderGeometry(0.11, 0.11, 0.03, 14);
    case "sprinkles": return new THREE.CapsuleGeometry(0.016, 0.07, 2, 6).rotateZ(Math.PI / 2);
    case "bubbles": return new THREE.SphereGeometry(0.07, 10, 8);
    case "orbs": return new THREE.SphereGeometry(0.07, 12, 10);
    case "bolts": {
      const shape = new THREE.Shape();
      shape.moveTo(0, 0.42); shape.lineTo(0.09, 0.2); shape.lineTo(0.02, 0.2);
      shape.lineTo(0.08, 0); shape.lineTo(-0.06, 0.24); shape.lineTo(0.01, 0.24); shape.lineTo(-0.05, 0.42);
      return new THREE.ExtrudeGeometry(shape, { depth: 0.03, bevelEnabled: false }).translate(0, 0, -0.015);
    }
    default: return undefined;
  }
}

/** Seeded per-visual random so decorations look the same every time. */
function rng(seed: number) {
  let s = seed >>> 0 || 1;
  return () => ((s = Math.imul(s ^ (s >>> 15), 0x2c1b3c6d) + 0x9e3779b9) >>> 0) / 4294967296;
}

export interface HoleVisualOptions {
  /** Draw the shader abyss (needs the ground to be cut open at this hole). */
  abyss?: boolean;
  /** Emit ambient particles + trail into this effects system. */
  effects?: Effects;
  /** Lower particle rates for crowded scenes (bots). */
  particleScale?: number;
}

export class HoleVisual {
  readonly group = new THREE.Group();
  readonly skin: SkinDefinition;
  /** Uniform-scaled part: rim, decorations, ground glow. */
  private readonly top = new THREE.Group();
  /** Scaled (r, 1, r) so the depth stays constant. */
  private readonly abyss = new THREE.Group();
  private readonly decoPivot = new THREE.Group();
  private readonly abyssMaterial?: THREE.ShaderMaterial;
  private readonly glowMaterial: THREE.ShaderMaterial;
  private readonly rimMaterial: THREE.MeshStandardMaterial;
  private readonly deco?: THREE.InstancedMesh;
  private readonly decoBase: THREE.Matrix4[] = [];
  private readonly options: HoleVisualOptions;
  private readonly disposables: Array<{ dispose(): void }> = [];
  private readonly random: () => number;
  private emitAccumulator = 0;
  private trailAccumulator = 0;
  private pulse = 0;
  private time = 0;
  private lastX = 0;
  private lastZ = 0;
  private radius = 1;
  /** Network-driven holes glide towards these (see followTarget). */
  targetX = 0;
  targetZ = 0;
  targetRadius = 1;
  private followInit = false;
  private fx = 0;
  private fz = 0;
  private fr = 1;
  private readonly tmp = new THREE.Matrix4();
  private readonly tmpQ = new THREE.Quaternion();
  private readonly tmpV = new THREE.Vector3();
  private readonly tmpS = new THREE.Vector3();

  constructor(skin: SkinDefinition, options: HoleVisualOptions = {}) {
    this.skin = skin;
    this.options = options;
    this.random = rng(skin.id.length * 7919 + skin.price);
    this.group.add(this.abyss, this.top);

    if (options.abyss !== false) {
      this.abyssMaterial = new THREE.ShaderMaterial({
        vertexShader: ABYSS_VERT,
        fragmentShader: ABYSS_FRAG,
        side: THREE.BackSide,
        uniforms: {
          uTime: { value: 0 },
          uA: { value: new THREE.Color(skin.inner.a) },
          uB: { value: new THREE.Color(skin.inner.b) },
          uKind: { value: INNER_KIND[skin.inner.kind] },
          uDepth: { value: ABYSS_DEPTH },
        },
      });
      const wall = new THREE.Mesh(new THREE.CylinderGeometry(0.995, 0.995, ABYSS_DEPTH, 48, 1, true), this.abyssMaterial);
      wall.position.y = -ABYSS_DEPTH / 2 + 0.02;
      const bottom = new THREE.Mesh(
        new THREE.CircleGeometry(1, 32),
        new THREE.MeshBasicMaterial({ color: 0x000000 }),
      );
      bottom.rotation.x = -Math.PI / 2;
      bottom.position.y = -ABYSS_DEPTH + 0.05;
      this.abyss.add(wall, bottom);
      this.disposables.push(wall.geometry, this.abyssMaterial, bottom.geometry, bottom.material as THREE.Material);
    } else {
      // Flat preview fallback: a dark disc.
      const disc = new THREE.Mesh(new THREE.CircleGeometry(1, 48), new THREE.MeshBasicMaterial({ color: skin.inner.a }));
      disc.rotation.x = -Math.PI / 2;
      disc.position.y = 0.01;
      this.top.add(disc);
      this.disposables.push(disc.geometry, disc.material as THREE.Material);
    }

    this.rimMaterial = new THREE.MeshStandardMaterial({
      color: skin.rim.color,
      emissive: skin.rim.emissive,
      emissiveIntensity: skin.rim.emissiveIntensity,
      metalness: skin.rim.metalness,
      roughness: skin.rim.roughness,
    });
    const rim = new THREE.Mesh(new THREE.TorusGeometry(1 + skin.rim.thickness * 0.4, skin.rim.thickness, 14, 96), this.rimMaterial);
    rim.rotation.x = Math.PI / 2;
    rim.position.y = 0.04;
    rim.castShadow = true;
    this.top.add(rim);
    this.disposables.push(rim.geometry, this.rimMaterial);

    if (skin.rim.outer) {
      const outerMaterial = new THREE.MeshStandardMaterial({
        color: skin.rim.outer, emissive: skin.rim.outer, emissiveIntensity: 0.35, roughness: 0.4, metalness: skin.rim.metalness * 0.6,
      });
      const outer = new THREE.Mesh(new THREE.TorusGeometry(1 + skin.rim.thickness * 2.3, skin.rim.thickness * 0.35, 8, 96), outerMaterial);
      outer.rotation.x = Math.PI / 2;
      outer.position.y = 0.03;
      this.top.add(outer);
      this.disposables.push(outer.geometry, outerMaterial);
    }

    this.glowMaterial = new THREE.ShaderMaterial({
      vertexShader: GLOW_VERT,
      fragmentShader: GLOW_FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(skin.glow.color) },
        uStrength: { value: skin.glow.strength },
        uPulse: { value: 0 },
      },
    });
    const glow = new THREE.Mesh(new THREE.RingGeometry(1, 1.6, 64, 1), this.glowMaterial);
    glow.rotation.x = -Math.PI / 2;
    glow.position.y = 0.03;
    glow.renderOrder = 2;
    this.top.add(glow);
    this.disposables.push(glow.geometry, this.glowMaterial);

    const geometry = decoGeometry(skin);
    if (geometry && skin.deco.count > 0) {
      const material = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: skin.deco.kind === "studs" ? 0.2 : 0.45,
        metalness: skin.deco.kind === "studs" ? 0.9 : 0.1,
        emissive: new THREE.Color(skin.deco.color),
        emissiveIntensity: ["orbs", "bolts", "crystals", "bubbles"].includes(skin.deco.kind) ? 0.8 : 0.08,
        transparent: skin.deco.kind === "bubbles" || skin.deco.kind === "crystals",
        opacity: skin.deco.kind === "bubbles" ? 0.75 : skin.deco.kind === "crystals" ? 0.85 : 1,
      });
      this.deco = new THREE.InstancedMesh(geometry, material, skin.deco.count);
      // Rim studs are tiny; a shadow pass per hole decoration is not worth the fill.
      this.deco.castShadow = false;
      const color = new THREE.Color();
      for (let i = 0; i < skin.deco.count; i++) {
        const angle = (i / skin.deco.count) * Math.PI * 2 + (skin.deco.kind === "sprinkles" ? this.random() * 0.12 : 0);
        const matrix = this.placeDeco(i, angle);
        this.decoBase.push(matrix);
        this.deco.setMatrixAt(i, matrix);
        const useAccent = skin.deco.accent && (skin.deco.kind === "sprinkles" ? this.random() < 0.45 : i % 2 === 1 && skin.deco.kind !== "pepperoni");
        color.set(useAccent ? skin.deco.accent! : skin.deco.color);
        this.deco.setColorAt(i, color);
      }
      this.decoPivot.add(this.deco);
      this.top.add(this.decoPivot);
      this.disposables.push(geometry, material);
    }
  }

  private placeDeco(i: number, angle: number): THREE.Matrix4 {
    const skin = this.skin;
    const ring = 1 + skin.rim.thickness * 0.4;
    const x = Math.cos(angle);
    const z = Math.sin(angle);
    const position = new THREE.Vector3(x * ring, 0.04 + skin.rim.thickness * 0.8, z * ring);
    const euler = new THREE.Euler(0, -angle, 0, "YXZ");
    const scale = new THREE.Vector3(1, 1, 1);
    switch (skin.deco.kind) {
      case "spikes":
      case "crystals":
        position.y = 0.04 + skin.rim.thickness * 0.4;
        euler.set(0, -angle, -0.55 - (skin.deco.kind === "crystals" ? (i % 3) * 0.12 : 0), "YXZ");
        scale.setScalar(skin.deco.kind === "crystals" ? 0.8 + (i % 3) * 0.25 : 1);
        break;
      case "petals":
        position.set(x * (ring + 0.16), 0.05, z * (ring + 0.16));
        euler.set(0, -angle, 0.12, "YXZ");
        break;
      case "pepperoni":
        position.y = 0.04 + skin.rim.thickness * 0.95;
        break;
      case "sprinkles":
        euler.set(this.random() * 0.4, -angle + (this.random() - 0.5) * 2.5, 0, "YXZ");
        position.y = 0.04 + skin.rim.thickness * 0.98;
        position.x += (this.random() - 0.5) * skin.rim.thickness;
        position.z += (this.random() - 0.5) * skin.rim.thickness;
        break;
      case "bolts":
        position.set(x * (ring + 0.25), 0.05, z * (ring + 0.25));
        euler.set(0, -angle + Math.PI / 2, 0, "YXZ");
        break;
      case "orbs":
        position.set(x * (ring + 0.2), 0.45, z * (ring + 0.2));
        scale.setScalar(1.4);
        break;
      case "bubbles":
        position.y = 0.06 + skin.rim.thickness;
        scale.setScalar(0.6 + this.random() * 0.8);
        break;
      default:
        break;
    }
    return new THREE.Matrix4().compose(position, new THREE.Quaternion().setFromEuler(euler), scale);
  }

  /** Smoothly follows targetX/Z/Radius (10 Hz snapshots -> 60 fps). */
  followTarget(dt: number) {
    if (!this.followInit) {
      this.followInit = true;
      this.fx = this.targetX; this.fz = this.targetZ; this.fr = this.targetRadius;
    }
    const k = 1 - Math.exp(-dt * 12);
    this.fx += (this.targetX - this.fx) * k;
    this.fz += (this.targetZ - this.fz) * k;
    this.fr += (this.targetRadius - this.fr) * k;
    this.update(dt, this.fx, this.fz, this.fr);
  }

  get x() { return this.group.position.x; }
  get z() { return this.group.position.z; }
  get currentRadius() { return this.radius; }

  /** Short bright pulse on the rim, e.g. when something was swallowed. */
  bump(strength = 1) {
    this.pulse = Math.min(1.4, this.pulse + 0.5 * strength);
  }

  /** Position in world space, radius in world units. */
  update(dt: number, x: number, z: number, radius: number) {
    this.time += dt;
    this.radius = radius;
    this.group.position.set(x, 0, z);
    // Off-screen holes keep their position for gameplay but skip shaders,
    // instanced deco and particle emission.
    if (!this.group.visible) return;
    this.pulse = Math.max(0, this.pulse - dt * 2.5);
    const breathe = 1 + Math.sin(this.time * 3) * 0.01 + this.pulse * 0.06;
    this.top.scale.setScalar(radius * breathe);
    this.abyss.scale.set(radius, 1, radius);
    if (this.abyssMaterial) this.abyssMaterial.uniforms.uTime!.value = this.time;
    this.glowMaterial.uniforms.uTime!.value = this.time;
    this.glowMaterial.uniforms.uPulse!.value = this.pulse;
    this.rimMaterial.emissiveIntensity = this.skin.rim.emissiveIntensity * (1 + this.pulse * 1.5);
    this.decoPivot.rotation.y += dt * this.skin.deco.spin;
    this.animateDeco();
    this.emitParticles(dt, x, z, radius);
    this.lastX = x;
    this.lastZ = z;
  }

  private animateDeco() {
    const deco = this.deco;
    if (!deco) return;
    const kind = this.skin.deco.kind;
    if (kind !== "orbs" && kind !== "bubbles" && kind !== "bolts") return;
    for (let i = 0; i < this.decoBase.length; i++) {
      this.decoBase[i]!.decompose(this.tmpV, this.tmpQ, this.tmpS);
      if (kind === "orbs") this.tmpV.y += Math.sin(this.time * 2.2 + i * 1.7) * 0.12;
      if (kind === "bubbles") {
        const k = (this.time * 0.6 + i * 0.37) % 1;
        this.tmpV.y += k * 0.25;
        this.tmpS.multiplyScalar(1 - k * 0.6);
      }
      if (kind === "bolts") this.tmpS.multiplyScalar(Math.sin(this.time * 9 + i * 2.3) > 0.2 ? 1 : 0.0001);
      this.tmp.compose(this.tmpV, this.tmpQ, this.tmpS);
      deco.setMatrixAt(i, this.tmp);
    }
    deco.instanceMatrix.needsUpdate = true;
  }

  private emitParticles(dt: number, x: number, z: number, radius: number) {
    const fx = this.options.effects;
    if (!fx || !this.group.visible) return;
    const scale = this.options.particleScale ?? 1;
    const p = this.skin.particles;
    if (p.kind !== "none" && p.rate > 0) {
      this.emitAccumulator += dt * p.rate * scale * (0.6 + radius * 0.18);
      while (this.emitAccumulator >= 1) {
        this.emitAccumulator -= 1;
        this.emitAmbient(fx, x, z, radius);
      }
    }
    // Trail: little glowing dots left behind while moving.
    const moved = Math.hypot(x - this.lastX, z - this.lastZ);
    if (dt > 0 && moved / dt > 1.5 && moved < 5) {
      this.trailAccumulator += moved * 1.6 * scale;
      const dx = (x - this.lastX) / moved;
      const dz = (z - this.lastZ) / moved;
      while (this.trailAccumulator >= 1) {
        this.trailAccumulator -= 1;
        const side = (this.random() - 0.5) * 1.6;
        fx.glow.emit({
          x: x - dx * radius * 1.05 - dz * side * radius * 0.6,
          y: 0.25,
          z: z - dz * radius * 1.05 + dx * side * radius * 0.6,
          color: this.skin.trail,
          size: 0.35 + radius * 0.06,
          sizeEnd: 0.2,
          life: 0.6,
          alpha: 0.55,
        });
      }
    }
  }

  private emitAmbient(fx: Effects, x: number, z: number, radius: number) {
    const p = this.skin.particles;
    const a = this.random() * Math.PI * 2;
    const r = radius * (1 + this.skin.rim.thickness * 0.4) + (this.random() - 0.3) * 0.4;
    const px = x + Math.cos(a) * r;
    const pz = z + Math.sin(a) * r;
    const out = { x: Math.cos(a), z: Math.sin(a) };
    const rand = this.random;
    switch (p.kind) {
      case "embers":
        fx.glow.emit({ x: px, y: 0.2, z: pz, vx: out.x * 0.3, vy: 1.6 + rand() * 1.8, vz: out.z * 0.3, color: p.color, size: 0.32, sizeEnd: 0.1, life: 1.1 + rand() * 0.6, drag: 0.4 });
        break;
      case "snow":
        fx.glow.emit({ x: px, y: 0.4 + rand() * 1.2, z: pz, vx: out.x * 0.5, vy: 0.2, vz: out.z * 0.5, color: p.color, size: 0.26, sizeEnd: 0.6, life: 1.6, gravity: 0.3, shape: Shape.Spark, spin: 1.5, alpha: 0.85 });
        break;
      case "sparkle":
        fx.glow.emit({ x: px, y: 0.3 + rand() * 0.8, z: pz, vy: 0.3, color: p.color, size: 0.5 + rand() * 0.3, sizeEnd: 0.1, life: 0.7, shape: Shape.Spark, spin: 2 });
        break;
      case "bubbles":
        fx.glow.emit({ x: px, y: 0.2, z: pz, vx: out.x * 0.2, vy: 0.9 + rand() * 0.7, vz: out.z * 0.2, color: p.color, size: 0.3 + rand() * 0.2, sizeEnd: 1.4, life: 1.0, shape: Shape.Ring, alpha: 0.8 });
        break;
      case "stars":
        fx.glow.emit({ x: px, y: 0.3, z: pz, vx: out.x * 0.4, vy: 0.6 + rand() * 0.8, vz: out.z * 0.4, color: p.color, size: 0.42, sizeEnd: 0.2, life: 1.3, shape: Shape.Spark, spin: 3 });
        break;
      case "leaves":
        fx.solid.emit({ x: px, y: 0.6 + rand(), z: pz, vx: out.x * 0.6 + (rand() - 0.5), vy: 0.1, vz: out.z * 0.6, color: p.color, size: 0.3, sizeEnd: 0.8, life: 1.5, gravity: 0.5, drag: 0.6, shape: Shape.Heart, spin: 3 });
        break;
      case "sparks":
        fx.glow.emit({ x: px, y: 0.25, z: pz, vx: out.x * (2 + rand() * 3), vy: 1 + rand() * 2, vz: out.z * (2 + rand() * 3), color: p.color, size: 0.22, sizeEnd: 0.1, life: 0.45, gravity: 6, drag: 1.5 });
        break;
      case "hearts":
        fx.glow.emit({ x: px, y: 0.3, z: pz, vy: 0.8, color: p.color, size: 0.45, sizeEnd: 0.6, life: 1.2, shape: Shape.Heart, alpha: 0.85 });
        break;
      default:
        break;
    }
  }

  /** Ring burst around the rim (eat feedback). */
  burst(count = 10) {
    const fx = this.options.effects;
    if (!fx) return;
    const { x, z } = this.group.position;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + this.random();
      const r = this.radius * 1.05;
      fx.glow.emit({
        x: x + Math.cos(a) * r, y: 0.3, z: z + Math.sin(a) * r,
        vx: Math.cos(a) * 3, vy: 1.5 + this.random() * 2, vz: Math.sin(a) * 3,
        color: this.skin.trail, size: 0.45, sizeEnd: 0.1, life: 0.5, drag: 2.5, gravity: 3, shape: Shape.Spark, spin: 4,
      });
    }
  }

  setVisible(visible: boolean) {
    this.group.visible = visible;
  }

  dispose() {
    this.group.removeFromParent();
    for (const item of this.disposables) item.dispose();
    this.deco?.dispose();
  }
}
